type NewPlayerStat = {
    name: string;
    value:   number;
  };
  
  type NewStatistics = {
    type: string;
    label: string;
    columns: {
      team: string;
      players: NewPlayerStat[];
    }[];
  };
  
  type OldPlayerStat = {
    playerHome: string;
    playerAway: string;
    home: number;
    away: number;
  };
  
  type OldStatistics = {
    type: string;
    label: string;
    rows: OldPlayerStat[];
  };
  
  export function switchDataStructureFromColumnsToRows(newStats: NewStatistics): OldStatistics {
    const homePlayers = newStats.columns.find(col => col.team === "Home")?.players || [];
    const awayPlayers = newStats.columns.find(col => col.team === "Away")?.players || [];
  
    // Sort players by home value descending
    homePlayers.sort((a, b) => b.value - a.value);
    awayPlayers.sort((a, b) => b.value - a.value);
  
    const rows: OldPlayerStat[] = [];
  
    // Creating rows, combining players from both teams
    const maxLength = Math.max(homePlayers.length, awayPlayers.length);
    for (let i = 0; i < maxLength; i++) {
      rows.push({
        playerHome: homePlayers[i]?.name || "",
        playerAway: awayPlayers[i]?.name || "",
        home: homePlayers[i]?.value || 0,
        away: awayPlayers[i]?.value || 0,
      });
    }
  
    return {
      type: newStats.type,
      label: newStats.label,
      rows: rows,
    };
  }



type PlayerData = {
  playerHome: string | null;
  playerAway: string | null;
  home: number | null;
  away: number | null;
};

// export function cleanPlayerData(data: PlayerData[]): PlayerData[] {
//   return data?.map(row => ({
//     playerHome: row.home === 0 || row.playerHome === '' ? null : row.playerHome,
//     playerAway: row.away === 0 || row.playerAway === '' ? null : row.playerAway,
//     home: row.home === 0 ? null : row.home,
//     away: row.away === 0 ? null : row.away,
//   }));
// }

export function cleanPlayerData(data: PlayerData[]): PlayerData[] {
  let firstZeroHomeFound = false;
  let firstZeroAwayFound = false;
  
  return data?.map(row => {
    // Check if it's the first occurrence of 0 for home
    if (row.home === 0 && !firstZeroHomeFound) {
      firstZeroHomeFound = true;
      return {
        playerHome: row.playerHome,
        playerAway: row.playerAway,
        home: row.home,
        away: row.away,
      };
    }
    
    // Check if it's the first occurrence of 0 for away
    if (row.away === 0 && !firstZeroAwayFound) {
      firstZeroAwayFound = true;
      return {
        playerHome: row.playerHome,
        playerAway: row.playerAway,
        home: row.home,
        away: row.away,
      };
    }
    
    // For all other cases (not-0 or consecutive 0)
    return {
      playerHome: row.home === 0 || row.playerHome === '' ? null : row.playerHome,
      playerAway: row.away === 0 || row.playerAway === '' ? null : row.playerAway,
      home: row.home === 0 ? null : row.home,
      away: row.away === 0 ? null : row.away,
    };
  });
}

export interface Player {
  name: string;
  value: number;
}

export interface TeamColumn {
  team: 'Home' | 'Away';
  players: Player[];
}

export interface PlayersComparisonData {
  type: string;
  label: string;
  columns: TeamColumn[];
}

export function addOppositeTeamWithZeroValues(columns: TeamColumn[]): TeamColumn[] {
  if (columns.length !== 1) {
    throw new Error('Array columns should contain exactly one object');
  }

  const existingTeam = columns[0];
  const oppositeTeamName = existingTeam.team === 'Home' ? 'Away' : 'Home';

  const oppositeTeam: TeamColumn = {
    team: oppositeTeamName,
    players: existingTeam.players.map(() => ({
      name: "",
      value: 0
    }))
  };

  return [...columns, oppositeTeam];
}


export const shortenFullNameForRow = (fullName: string | null, maxLength: number = 18): string => {
  if (!fullName) return '';
    const parts = fullName.split(' ');
    let lastName = parts.pop() || '';
    let firstNames = parts.join(' ');
  
    if (fullName.length <= maxLength) return fullName;
  
    let result = '';
  
    if (parts.length === 1) {
      // If there's only one first name
      const availableSpace = maxLength - lastName.length - 1; // 1 is the space between first name and last name
      if (availableSpace >= 5) { // 5 is the minimum length for "X..." (2 characters + 3 dots counted as 2)
        firstNames = firstNames.slice(0, availableSpace - 2) + '...';
      } else {
        firstNames = firstNames[0] + '.';
      }
    } else if (parts.length > 1) {
      firstNames = parts.map(name => name[0] + '.').join(' ');
    }
  
    result = `${firstNames} ${lastName}`.trim();
  
    // Shorten the last name only if the first name is already maximally shortened (to 2 characters or less)
    if (result.length > maxLength && firstNames.length <= 2) {
      const availableSpace = maxLength - firstNames.length - 1; // 1 is the space
      lastName = lastName.slice(0, availableSpace - 3) + '...';
      result = `${firstNames} ${lastName}`.trim();
    }
  
    return result;
  };
  